import type { ReactNode } from "react";

/**
 * Разбор «богатого» текста из контент-файлов.
 *
 * В JSON пишем `Превратите данные **вашей CRM** в прибыль`,
 * а компонент подсвечивает фрагмент между `**` акцентным цветом.
 * Это удобно для админки: редактор просто оборачивает слова в звёздочки.
 */
export function parseRich(text: string): { text: string; accent: boolean }[] {
  if (!text) return [];
  return text.split("**").map((part, index) => ({
    text: part,
    accent: index % 2 === 1,
  }));
}

export function Rich({
  text,
  accentClassName = "text-blue-600",
}: {
  text: string;
  accentClassName?: string;
}) {
  const parts = parseRich(text);
  return (
    <>
      {parts.map((part, index) =>
        part.accent ? (
          <span key={index} className={accentClassName}>
            {part.text}
          </span>
        ) : (
          <span key={index}>{part.text}</span>
        )
      )}
    </>
  );
}

/**
 * Подстановка `{{ключ}}` в строках шаблонов из контент-файлов.
 * Например: `За год это {{annual}}, которые вы теряете`.
 */
export function interpolate(
  template: string,
  values: Record<string, string | number>
): string {
  if (!template) return "";
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match
  );
}

/** Рендер строки-шаблона с подстановкой и подсветкой **акцентов** */
export function RichTemplate({
  template,
  values,
  accentClassName,
}: {
  template: string;
  values: Record<string, string | number>;
  accentClassName?: string;
}): ReactNode {
  return <Rich text={interpolate(template, values)} accentClassName={accentClassName} />;
}

/** Класс фона по «тону» из контента (устойчив к опечаткам в админке) */
const TONE_CLASSES: Record<string, string> = {
  "blue-600": "bg-blue-600",
  "blue-500": "bg-blue-500",
  "blue-400": "bg-blue-400",
  "slate-400": "bg-slate-400",
  "slate-300": "bg-slate-300",
  "emerald-500": "bg-emerald-500",
  "orange-600": "bg-orange-600",
  "orange-500": "bg-orange-500",
  "amber-500": "bg-amber-500",
  "stone-800": "bg-stone-800",
  "stone-700": "bg-stone-700",
  "stone-600": "bg-stone-600",
  "stone-500": "bg-stone-500",
};

export function toneClass(tone: string | undefined, fallback = "bg-blue-600"): string {
  if (!tone) return fallback;
  return TONE_CLASSES[tone] ?? fallback;
}

/** Простые форматтеры */
export function formatMoney(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} млн ₽`;
  return `${Math.round(value / 1000)} тыс ₽`;
}

export function formatThousands(value: number): string {
  return value.toLocaleString("ru-RU");
}
